import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { EyeOff, Images } from 'lucide-react';
import { Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Modal } from '../../componentes/sobreposicao';
import { useDiscreto } from '../../componentes/sensivel';
import { get } from '../../nucleo/api';
import { dataHora } from '../../nucleo/formato';
import type { HistoricoTelas as DadosHistorico, ItemHistoricoTelas } from './tipos';

/** Endereço da imagem pela rota autenticada (`antes/01-inicio.jpg` vira `/api/v1/admin/historico-telas/antes/01-inicio.jpg`). */
export function urlImagemHistorico(caminho: string): string {
  const [lado, arquivo] = caminho.split('/') as [string, string];
  return `/api/v1/admin/historico-telas/${encodeURIComponent(lado)}/${encodeURIComponent(arquivo)}`;
}

type Ampliada = { caminho: string; titulo: string };

/** Uma coluna (antes ou depois): as imagens empilhadas, cada uma abre ampliada; sem imagem, o aviso curto. */
function Coluna({ rotulo, imagens, vazio, titulo, celular, aoAmpliar }: { rotulo: string; imagens: string[]; vazio: string; titulo: string; celular?: boolean; aoAmpliar: (a: Ampliada) => void }) {
  const { t } = useTranslation('admin');
  const { ativo: discreto } = useDiscreto();
  return (
    <div className="flex min-w-0 flex-col gap-2" data-historico-coluna>
      <h3 className="text-xs font-semibold tracking-wide text-tinta-3 uppercase">{rotulo}</h3>
      {!imagens.length && <p className="rounded-lg border border-dashed border-borda px-3 py-6 text-center text-sm text-tinta-3">{vazio}</p>}
      {imagens.map((c, i) => {
        const nome = imagens.length > 1 ? t('historico.imagemN', { titulo, rotulo, n: i + 1, total: imagens.length }) : t('historico.imagem', { titulo, rotulo });
        // No modo discreto, a imagem fica de fora (o "antes" pode ter dado real; a tela pode estar compartilhada).
        if (discreto)
          return (
            <p key={c} className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-borda px-3 py-6 text-center text-sm text-tinta-3">
              <EyeOff className="size-4 shrink-0" aria-hidden />
              {t('historico.oculta')}
            </p>
          );
        return (
          <button key={c} type="button" data-historico-imagem={c} onClick={() => aoAmpliar({ caminho: c, titulo: nome })} aria-label={t('historico.ampliar', { nome })} className={`block w-full min-w-0 cursor-zoom-in rounded-lg ${celular ? 'max-w-60' : ''}`}>
            <img src={urlImagemHistorico(c)} alt={nome} loading="lazy" decoding="async" className="block h-auto w-full rounded-lg border border-borda" />
          </button>
        );
      })}
    </div>
  );
}

function Item({ item, aoAmpliar }: { item: ItemHistoricoTelas; aoAmpliar: (a: Ampliada) => void }) {
  const { t } = useTranslation('admin');
  const titulo = item.numero ? `${item.numero} · ${item.titulo}` : item.titulo;
  const temCelular = item.celular_antes.length > 0 || item.celular_depois.length > 0;
  return (
    <div id={`historico-${item.id}`} data-historico-item={item.id} className="scroll-mt-4">
      <Cartao titulo={titulo}>
        <div className="flex flex-col gap-4">
          {item.pedido && <p className="text-sm text-tinta-2">{item.pedido}</p>}
          {/* Antes e depois lado a lado; no celular, um embaixo do outro. */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Coluna rotulo={t('historico.antes')} imagens={item.antes} vazio={t('historico.semAntes')} titulo={item.titulo} aoAmpliar={aoAmpliar} />
            <Coluna rotulo={t('historico.depois')} imagens={item.depois} vazio={t('historico.semCaptura')} titulo={item.titulo} aoAmpliar={aoAmpliar} />
          </div>
          {temCelular && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Coluna rotulo={t('historico.celularAntes')} imagens={item.celular_antes} vazio={t('historico.semCaptura')} titulo={item.titulo} celular aoAmpliar={aoAmpliar} />
              <Coluna rotulo={t('historico.celularDepois')} imagens={item.celular_depois} vazio={t('historico.semCaptura')} titulo={item.titulo} celular aoAmpliar={aoAmpliar} />
            </div>
          )}
          {item.legenda && <p className="rounded-lg bg-sup-2 px-3 py-2 text-sm text-tinta-1">{item.legenda}</p>}
        </div>
      </Cartao>
    </div>
  );
}

/**
 * Fase 2 (W37): Admin > Painel admin > Histórico telas. O comparativo "antes e depois" das telas, do catálogo do servidor
 * (fora do repositório e do bundle). As legendas vêm do catálogo, em pt; só os rótulos da página mudam com o idioma.
 */
export function HistoricoTelas() {
  const { t } = useTranslation('admin');
  const q = useQuery({ queryKey: ['/admin/historico-telas'], queryFn: () => get<DadosHistorico>('/admin/historico-telas'), staleTime: 5 * 60_000 });
  const [ampliada, setAmpliada] = useState<Ampliada | null>(null);
  return (
    <>
      <Estado consulta={q}>
        {(d) =>
          !d.disponivel || !d.itens.length ? (
            <Cartao>
              <div data-historico-vazio>
                <Vazio icone={<Images className="size-5" aria-hidden />} titulo={t('historico.vazioTitulo')} texto={d.disponivel ? t('historico.vazioSemItens') : t('historico.vazioTexto')} />
              </div>
            </Cartao>
          ) : (
            <div className="flex flex-col gap-5" data-historico-lista>
              <p className="text-sm text-tinta-2">
                {t('historico.intro', { count: d.itens.length })}
                {d.gerado_em && <> {t('historico.geradoEm', { quando: dataHora(d.gerado_em) })}</>}
              </p>
              {d.itens.map((i) => (
                <Item key={i.id} item={i} aoAmpliar={setAmpliada} />
              ))}
            </div>
          )
        }
      </Estado>
      <Modal aberto={!!ampliada} aoFechar={() => setAmpliada(null)} titulo={ampliada?.titulo ?? ''} largura="lg">
        {ampliada && <img src={urlImagemHistorico(ampliada.caminho)} alt={ampliada.titulo} data-historico-ampliada className="mx-auto block h-auto max-w-full rounded-lg border border-borda" />}
      </Modal>
    </>
  );
}
